import React, { FC } from 'react'
import { Icon, IconLegend, type IconType } from '@pandiafi/components'
import { useTranslation } from 'react-i18next'

import type { CoreParkingArea } from '../../../common/validations/contractParkingSpotsSchema'
import { spotIcons } from './uiSelectors'

const LEGEND_ENTRIES = [
  { icon: 'electricity', label: 'equipment.heating_outlet' },
  { icon: 'electric-charging', label: 'equipment.ev_charger' },
  { icon: 'inva', label: 'equipment.disabled_spot' },
  { icon: 'motorbike', label: 'equipment.motorbike_spot' },
  { icon: 'shelter', label: 'equipment.covered' },
  { icon: 'motorbike-shelter', label: 'equipment.covered_motorbike_spot' },
  { icon: 'inva-shelter', label: 'equipment.covered_disabled_spot' },
  { icon: 'garage', label: 'equipment.garage' },
  { icon: 'motorbike-garage', label: 'equipment.garage_motorbike_spot' },
  { icon: 'inva-garage', label: 'equipment.garage_disabled_spot' },
  { icon: 'hall', label: 'equipment.building' },
  { icon: 'motorbike-hall', label: 'equipment.building_motorbike_spot' },
  { icon: 'inva-hall', label: 'equipment.building_disabled_spot' },
] as const satisfies ReadonlyArray<{ icon: IconType; label: string }>

type Props = {
  areas: CoreParkingArea[]
}

export const ParkingSpotsLegend: FC<Props> = ({ areas }) => {
  const { t } = useTranslation('parking')

  const presentIcons = new Set<IconType>(
    areas
      .flatMap((area) => area.parkingSpots ?? [])
      .flatMap((spot) => {
        const { primary, secondary } = spotIcons(spot)
        return secondary ? [primary, secondary] : [primary]
      })
  )

  const visibleEntries = LEGEND_ENTRIES.filter((entry) =>
    presentIcons.has(entry.icon)
  )

  if (visibleEntries.length === 0) return null

  return (
    <>
      <a
        href="#icons"
        className="desktop:hidden sticky bottom-0 bg-common-gray-2 p-3 text-sm rounded-sm"
      >
        <Icon icon="info-circle" className="mr-2" />{' '}
        {t('legend.title', 'Icon explanations')}
      </a>

      <div id="icons" className="desktop:sticky bottom-0 bg-white">
        <IconLegend>
          {visibleEntries.map((entry) => (
            <IconLegend.Item key={entry.icon} icon={entry.icon}>
              {t(entry.label)}
            </IconLegend.Item>
          ))}
        </IconLegend>
      </div>
    </>
  )
}
